// La forma canónica que mandan las DOS pantallas públicas de entidades (el
// registro y la ficha), para que el backend reciba lo mismo venga de donde
// venga. Espejo de applicantPayload.js.

import { PHONE_TYPE } from "../../constants";
import {
  genContactId,
  normalizeContactEmails,
  normalizeContactPhones,
  normalizeCountryCode,
  toE164,
} from "../entities/entityLogic";

const trimmed = (v) => String(v == null ? "" : v).trim();
const orNull = (v) => trimmed(v) || null;

export const emptyEmailContact = () => ({
  id: genContactId(),
  address: "",
  label: "",
  primary: false,
  notify: true,
  verified: false,
});

export const emptyPhoneContact = (type = PHONE_TYPE.MOBILE) => ({
  id: genContactId(),
  number: "",
  type,
  extension: "",
  label: "",
  primary: false,
  verified: false,
});

// Lo que viaja a intakeEntity. Los contactos van normalizados (minúsculas,
// E.164) para que lo que el formulario cree haber mandado sea exactamente lo que
// el backend compara contra la puerta de verificación: si el cliente mandara
// "300 111 2233" y el backend guardara "+573001112233", la comparación con el
// número del token de Phone Auth fallaría por un espacio.
//
// `verified` viaja pero el backend lo ignora: sólo él decide qué está verificado,
// a partir del OTP y del token de Phone Auth.
export const buildEntityData = (form) => ({
  nit: trimmed(form.nit),
  legalName: trimmed(form.legalName),
  tradeName: orNull(form.tradeName),
  entityType: form.entityType,
  sector: orNull(form.sector),
  country: trimmed(form.country) || "Colombia",
  department: orNull(form.department),
  city: orNull(form.city),
  address: orNull(form.address),
  website: orNull(form.website),

  legalRepName: orNull(form.legalRepName),
  legalRepIdType: orNull(form.legalRepIdType),
  legalRepIdNumber: orNull(form.legalRepIdNumber),
  legalRepEmail: orNull(form.legalRepEmail),
  // El teléfono del representante viaja ya en E.164 (lo compone el selector de
  // país) y su país al lado: el indicativo solo no basta para saber cuál es.
  legalRepPhone: orNull(form.legalRepPhone),
  legalRepPhoneCountryCode: normalizeCountryCode(form.legalRepPhoneCountryCode),

  contactEmails: normalizeContactEmails(form.contactEmails || []),
  contactPhones: normalizeContactPhones(form.contactPhones || []),
});

// El camino inverso: el documento guardado → el estado del formulario. Los
// campos de texto nunca son null (React se queja de un input que pasa de
// no-controlado a controlado).
export const entityToForm = (entity) => ({
  nit: entity?.nit || "",
  legalName: entity?.legalName || "",
  tradeName: entity?.tradeName || "",
  entityType: entity?.entityType || "",
  sector: entity?.sector || "",
  country: entity?.country || "Colombia",
  department: entity?.department || "",
  city: entity?.city || "",
  address: entity?.address || "",
  website: entity?.website || "",

  legalRepName: entity?.legalRepName || "",
  legalRepIdType: entity?.legalRepIdType || "",
  legalRepIdNumber: entity?.legalRepIdNumber || "",
  legalRepEmail: entity?.legalRepEmail || "",
  legalRepPhone: entity?.legalRepPhone || "",
  legalRepPhoneCountryCode: entity?.legalRepPhoneCountryCode || "",

  contactEmails: (entity?.contactEmails || []).map((c) => ({
    ...c,
    label: c.label || "",
  })),
  contactPhones: (entity?.contactPhones || []).map((c) => ({
    ...c,
    label: c.label || "",
    extension: c.extension || "",
  })),
});

// El par de contacto del REGISTRO, donde sólo se pide uno de cada. Se arma
// aparte porque ahí los dos son obligatorios y primarios por definición.
//
// `name` es el nombre de quien registra, y va como `label` en las dos filas:
// todo contacto tiene que decir de quién es. El `userId` no se puede poner
// todavía —la cuenta se crea en el servidor, después de guardar la entidad—, así
// que intakeEntity se lo estampa encima cuando ya existe. Entre lo uno y lo
// otro, y también si la cuenta no llega a crearse, la fila igual dice a nombre
// de quién va.
export const registrationContacts = ({ email, phone, phoneCountry, name }) => ({
  contactEmails: [
    { id: genContactId(), address: trimmed(email), label: orNull(name), primary: true, notify: true },
  ],
  contactPhones: [
    {
      id: genContactId(),
      // `phone` ya llega compuesto por el selector de país; toE164 lo deja igual
      // porque empieza por "+", y sigue aquí para el caso de que alguien mande
      // un número nacional suelto.
      number: toE164(phone),
      type: PHONE_TYPE.MOBILE,
      countryCode: phoneCountry || null,
      label: orNull(name),
      primary: true,
    },
  ],
});

export { toE164 };
